import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Plus, Settings, GraduationCap, BookOpen, Target } from 'lucide-react';
import { toast } from 'sonner';

const GestionEstructura = () => {
  const navigate = useNavigate();
  
  const [niveles, setNiveles] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [ras, setRas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showNivelForm, setShowNivelForm] = useState(false);
  const [showEspecialidadForm, setShowEspecialidadForm] = useState(false);
  const [showRAForm, setShowRAForm] = useState(false);
  
  const [nivelForm, setNivelForm] = useState({ nombre: '', descripcion: '' });
  const [especialidadForm, setEspecialidadForm] = useState({ nombre: '', descripcion: '' });
  const [raForm, setRAForm] = useState({ codigo: '', descripcion: '', especialidad_id: '', nivel_id: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [nivelesRes, especialidadesRes, rasRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/niveles`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/especialidades`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/resultados-aprendizaje`, { withCredentials: true })
      ]);
      
      setNiveles(nivelesRes.data);
      setEspecialidades(especialidadesRes.data);
      setRas(rasRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNivel = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/niveles`,
        nivelForm,
        { withCredentials: true }
      );
      toast.success('Nivel creado exitosamente');
      setNivelForm({ nombre: '', descripcion: '' });
      setShowNivelForm(false);
      fetchData();
    } catch (error) {
      toast.error('Error al crear nivel');
    }
  };

  const handleCreateEspecialidad = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/especialidades`,
        especialidadForm,
        { withCredentials: true }
      );
      toast.success('Especialidad creada exitosamente');
      setEspecialidadForm({ nombre: '', descripcion: '' });
      setShowEspecialidadForm(false);
      fetchData();
    } catch (error) {
      toast.error('Error al crear especialidad');
    }
  };

  const handleCreateRA = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/resultados-aprendizaje`,
        raForm,
        { withCredentials: true }
      );
      toast.success('Resultado de Aprendizaje creado exitosamente');
      setRAForm({ codigo: '', descripcion: '', especialidad_id: '', nivel_id: '' });
      setShowRAForm(false);
      fetchData();
    } catch (error) {
      toast.error('Error al crear RA');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Estructura Académica</h1>
              <p className="text-sm text-muted-foreground">Gestionar niveles, especialidades y resultados de aprendizaje</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="niveles" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="niveles" data-testid="tab-niveles">
              <GraduationCap className="h-4 w-4 mr-2" />
              Niveles
            </TabsTrigger>
            <TabsTrigger value="especialidades" data-testid="tab-especialidades">
              <BookOpen className="h-4 w-4 mr-2" />
              Especialidades
            </TabsTrigger>
            <TabsTrigger value="ras" data-testid="tab-ras">
              <Target className="h-4 w-4 mr-2" />
              Resultados de Aprendizaje
            </TabsTrigger>
          </TabsList>

          {/* NIVELES */}
          <TabsContent value="niveles">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Niveles Educativos</CardTitle>
                    <CardDescription>Gestionar los niveles del sistema educativo</CardDescription>
                  </div>
                  <Button onClick={() => setShowNivelForm(!showNivelForm)} data-testid="btn-agregar-nivel">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Nivel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showNivelForm && (
                  <form onSubmit={handleCreateNivel} className="mb-6 p-4 border border-border rounded-lg space-y-4">
                    <div>
                      <Label htmlFor="nivel-nombre">Nombre del Nivel</Label>
                      <Input
                        id="nivel-nombre"
                        value={nivelForm.nombre}
                        onChange={(e) => setNivelForm({ ...nivelForm, nombre: e.target.value })}
                        placeholder="Ej: Décimo Año"
                        required
                        data-testid="input-nivel-nombre"
                      />
                    </div>
                    <div>
                      <Label htmlFor="nivel-descripcion">Descripción</Label>
                      <Input
                        id="nivel-descripcion"
                        value={nivelForm.descripcion}
                        onChange={(e) => setNivelForm({ ...nivelForm, descripcion: e.target.value })}
                        placeholder="Descripción opcional"
                        data-testid="input-nivel-descripcion"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" data-testid="btn-guardar-nivel">Guardar</Button>
                      <Button type="button" variant="outline" onClick={() => setShowNivelForm(false)}>Cancelar</Button>
                    </div>
                  </form>
                )}

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto"></div>
                  </div>
                ) : niveles.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay niveles registrados</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {niveles.map((nivel) => (
                        <TableRow key={nivel.nivel_id}>
                          <TableCell className="font-medium">{nivel.nombre}</TableCell>
                          <TableCell>{nivel.descripcion || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={nivel.activo ? "default" : "secondary"}>
                              {nivel.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ESPECIALIDADES */}
          <TabsContent value="especialidades">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Especialidades</CardTitle>
                    <CardDescription>Gestionar especialidades técnicas</CardDescription>
                  </div>
                  <Button onClick={() => setShowEspecialidadForm(!showEspecialidadForm)} data-testid="btn-agregar-especialidad">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Especialidad
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showEspecialidadForm && (
                  <form onSubmit={handleCreateEspecialidad} className="mb-6 p-4 border border-border rounded-lg space-y-4">
                    <div>
                      <Label htmlFor="esp-nombre">Nombre de la Especialidad</Label>
                      <Input
                        id="esp-nombre"
                        value={especialidadForm.nombre}
                        onChange={(e) => setEspecialidadForm({ ...especialidadForm, nombre: e.target.value })}
                        placeholder="Ej: Informática en Desarrollo de Software"
                        required
                        data-testid="input-especialidad-nombre"
                      />
                    </div>
                    <div>
                      <Label htmlFor="esp-descripcion">Descripción</Label>
                      <Input
                        id="esp-descripcion"
                        value={especialidadForm.descripcion}
                        onChange={(e) => setEspecialidadForm({ ...especialidadForm, descripcion: e.target.value })}
                        placeholder="Descripción opcional"
                        data-testid="input-especialidad-descripcion"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" data-testid="btn-guardar-especialidad">Guardar</Button>
                      <Button type="button" variant="outline" onClick={() => setShowEspecialidadForm(false)}>Cancelar</Button>
                    </div>
                  </form>
                )}

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto"></div>
                  </div>
                ) : especialidades.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay especialidades registradas</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {especialidades.map((esp) => (
                        <TableRow key={esp.especialidad_id}>
                          <TableCell className="font-medium">{esp.nombre}</TableCell>
                          <TableCell>{esp.descripcion || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={esp.activo ? "default" : "secondary"}>
                              {esp.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* RESULTADOS DE APRENDIZAJE */}
          <TabsContent value="ras">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Resultados de Aprendizaje</CardTitle>
                    <CardDescription>Definir competencias y objetivos de aprendizaje</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowRAForm(!showRAForm)} 
                    disabled={niveles.length === 0 || especialidades.length === 0}
                    data-testid="btn-agregar-ra"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar RA
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showRAForm && (
                  <form onSubmit={handleCreateRA} className="mb-6 p-4 border border-border rounded-lg space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ra-codigo">Código del RA</Label>
                        <Input
                          id="ra-codigo"
                          value={raForm.codigo}
                          onChange={(e) => setRAForm({ ...raForm, codigo: e.target.value })}
                          placeholder="Ej: RA-INF-01"
                          required
                          data-testid="input-ra-codigo"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ra-nivel">Nivel</Label>
                        <select
                          id="ra-nivel"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={raForm.nivel_id}
                          onChange={(e) => setRAForm({ ...raForm, nivel_id: e.target.value })}
                          required
                          data-testid="select-ra-nivel"
                        >
                          <option value="">Seleccionar nivel</option>
                          {niveles.map(n => (
                            <option key={n.nivel_id} value={n.nivel_id}>{n.nombre}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="ra-especialidad">Especialidad</Label>
                      <select
                        id="ra-especialidad"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={raForm.especialidad_id}
                        onChange={(e) => setRAForm({ ...raForm, especialidad_id: e.target.value })}
                        required
                        data-testid="select-ra-especialidad"
                      >
                        <option value="">Seleccionar especialidad</option>
                        {especialidades.map(e => (
                          <option key={e.especialidad_id} value={e.especialidad_id}>{e.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="ra-descripcion">Descripción del RA</Label>
                      <textarea
                        id="ra-descripcion"
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={raForm.descripcion}
                        onChange={(e) => setRAForm({ ...raForm, descripcion: e.target.value })}
                        placeholder="Describe el resultado de aprendizaje esperado"
                        required
                        data-testid="textarea-ra-descripcion"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" data-testid="btn-guardar-ra">Guardar</Button>
                      <Button type="button" variant="outline" onClick={() => setShowRAForm(false)}>Cancelar</Button>
                    </div>
                  </form>
                )}

                {niveles.length === 0 || especialidades.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-border rounded-lg">
                    <Settings className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-muted-foreground">Primero debes crear Niveles y Especialidades</p>
                  </div>
                ) : loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto"></div>
                  </div>
                ) : ras.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay RAs registrados</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead>Nivel</TableHead>
                        <TableHead>Especialidad</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ras.map((ra) => {
                        const nivel = niveles.find(n => n.nivel_id === ra.nivel_id);
                        const especialidad = especialidades.find(e => e.especialidad_id === ra.especialidad_id);
                        return (
                          <TableRow key={ra.ra_id}>
                            <TableCell className="font-medium">{ra.codigo}</TableCell>
                            <TableCell className="max-w-md truncate">{ra.descripcion}</TableCell>
                            <TableCell>{nivel?.nombre || '-'}</TableCell>
                            <TableCell>{especialidad?.nombre || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={ra.activo ? "default" : "secondary"}>
                                {ra.activo ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default GestionEstructura;
